import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './tailwind-palette-builder.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

interface State {
    baseColor: string;
    paletteName: string;
    copied: 'config' | 'css' | null;
}

function hexToHsl(hex: string): [number, number, number] {
    const m = hex.replace('#', '').match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
    if (!m) return [0, 0, 50];
    let h = m[0];
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const r = parseInt(h.substring(0, 2), 16) / 255;
    const g = parseInt(h.substring(2, 4), 16) / 255;
    const b = parseInt(h.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let hue = 0; const lightness = (max + min) / 2;
    let saturation = 0;
    if (max !== min) {
        const d = max - min;
        saturation = lightness > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: hue = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
            case g: hue = ((b - r) / d + 2) * 60; break;
            case b: hue = ((r - g) / d + 4) * 60; break;
        }
    }
    return [hue, saturation * 100, lightness * 100];
}

function hslToHex(h: number, s: number, l: number): string {
    s /= 100; l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
    return '#' + toHex(f(0)) + toHex(f(8)) + toHex(f(4));
}

const STEP_LIGHTNESS: Record<number, number> = {
    50: 96, 100: 92, 200: 84, 300: 75, 400: 65,
    500: 55, 600: 47, 700: 39, 800: 30, 900: 22, 950: 15,
};

export default class TailwindPaletteBuilderComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = { baseColor: '#6366f1', paletteName: 'brand', copied: null };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get palette(): { step: number; hex: string }[] {
        const [h, s] = hexToHsl(this.state.baseColor);
        return STEPS.map(step => ({ step, hex: hslToHex(h, s, STEP_LIGHTNESS[step]) }));
    }

    private get configCode(): string {
        return `// tailwind.config.js\nmodule.exports = {\n  theme: {\n    extend: {\n      colors: {\n        ${this.state.paletteName}: {\n${this.palette.map(p => `          ${p.step}: '${p.hex}',`).join('\n')}\n        }\n      }\n    }\n  }\n};`;
    }

    private get cssCode(): string {
        return `:root {\n${this.palette.map(p => `  --${this.state.paletteName}-${p.step}: ${p.hex};`).join('\n')}\n}`;
    }

    copy = (kind: 'config' | 'css') => {
        navigator.clipboard.writeText(kind === 'config' ? this.configCode : this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: kind });
            this.copyTimeout = setTimeout(() => this.setState({ copied: null }), 2000);
        });
    };

    render() {
        const { baseColor, paletteName, copied } = this.state;
        const palette = this.palette;
        return (
            <div className="row">
                <div className="col-12 col-md-5">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>
                            Base color (used as-is for the 500 step)
                            <div className={styles.colorRow}>
                                <input type="color" value={baseColor}
                                    onChange={e => this.setState({ baseColor: e.target.value })} />
                                <input className={styles.input} type="text" value={baseColor}
                                    onChange={e => this.setState({ baseColor: e.target.value })} />
                            </div>
                        </label>
                        <label className={sharedStyles.label}>
                            Palette name
                            <input className={styles.input} type="text" value={paletteName}
                                onChange={e => this.setState({ paletteName: e.target.value.replace(/[^a-zA-Z0-9-]/g, '') || 'brand' })} />
                        </label>
                        <p className={styles.note}>
                            Lightness scale follows Tailwind v3.x defaults (50→950). For perceptually-uniform palettes, use the
                            Material color tool instead.
                        </p>
                    </div>
                </div>
                <div className="col-12 col-md-7">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Palette</label>
                        <div className={styles.swatchGrid}>
                            {palette.map(p => (
                                <button key={p.step} type="button" className={styles.swatch} style={{ background: p.hex }}
                                    title={`Click to copy ${p.hex}`}
                                    onClick={() => navigator.clipboard?.writeText(p.hex)}>
                                    <span className={styles.step}>{p.step}</span>
                                    <span className={styles.hex}>{p.hex}</span>
                                </button>
                            ))}
                        </div>
                        <div className={styles.copyRow}>
                            <button className={`${sharedStyles.copyButton} ${copied === 'config' ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={() => this.copy('config')}>
                                {copied === 'config' ? '✓ Copied' : 'Copy tailwind.config.js'}
                            </button>
                            <button className={`${sharedStyles.copyButton} ${copied === 'css' ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={() => this.copy('css')}>
                                {copied === 'css' ? '✓ Copied' : 'Copy CSS variables'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
